import React, { memo, useCallback } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useRequest } from 'ahooks';
// import { IChartProps, MarkerCallback } from 'bizcharts/lib/interface';
import { IChartProps } from 'bizcharts/lib/interface';
import moment from 'moment';
import { ConBrandDetailsWebController } from '@/services/swagger/ConBrandDetailsWebController';
import MapChart from '@/components/charts/map';
import Pie from '@/components/charts/pie';
import Bar from '@/components/charts/bar';
import TitleTag from '@/components/titletag';
// import LineChart from '@/components/charts/line';
import ChartHeader from '@/business-components/chart-header';
import { RootState } from '@/models';
import Chart from '@/business-components/chart';
import LockedCard, { LockCardState } from '@/business-components/LockedCard';
import { ConBrandFansVo, UserNumTypeVo } from '@/services/swagger/data-contracts';
import LineAndBarMixedChart from '@/components/charts/LineAndBarMixedChart';
import TooltipBase from '@/components/tooltip';
import CustomEmpty from '@/business-components/CustomEmpty';
import styles from './index.module.scss';

const brandDetailController = new ConBrandDetailsWebController();

const scale: IChartProps['scale'] = {
  countTimeMonth: {
    type: 'cat',
    alias: '月份',
    formatter(value) {
      return `${value}月`;
    },
  },
  contrastChangeValue: {
    type: 'linear',
    min: 0,
    max: 100,
    tickCount: 2,
    nice: true,
    alias: '新增投诉指数增速',
    formatter(value) {
      return `${value}%`;
    },
  },
  consumerComplaintNum: {
    type: 'linear',
    // tickCount: 2,
    nice: true,
    alias: '新增投诉指数',
  },
};
const searchClotChartScale: IChartProps['scale'] = {
  countTimeStr: {
    type: 'cat',
    alias: '月份',
  },
  value: {
    type: 'linear',
    //   tickCount: 2,
    nice: true,
    alias: '',
  },
};
const colors = ['#f75c2f', '#ffc2b0', '#666666', '#b8b0ad'];
// const symbol: MarkerCallback = (x: number, y: number) => {
//   const r = 2;
//   return [
//     ['M', x - 4 * r, y],
//     ['L', x + 4 * r, y],
//     ['M', x - r, y],
//     ['A', r, r, 0, 0, 0, x + r, y],
//     ['A', r, r, 0, 0, 0, x - r, y],
//   ];
// };

export type DevelopmentProps = {
  /** 品牌id */
  conBrandId: string;
  /** 品牌解锁状态 */
  brandLockState?: string;
  /** 官号总粉丝规模指数 */
  totalFans?: number;
  /** 官媒粉丝数 */
  fansList: UserNumTypeVo[];
  /** 社交平台官号粉丝数量占比 */
  fansRateList: ConBrandFansVo[];
  /** 品牌解锁状态改变回调 */
  onLockedCardStateChange?: (state: string) => void;
};

const Development: React.FC<DevelopmentProps> = ({
  conBrandId,
  brandLockState = 'not_member',
  totalFans,
  fansList = [],
  fansRateList = [],
  onLockedCardStateChange,
}) => {
  const { token } = useSelector((state: RootState) => state.user);

  const onStateChange = useCallback(
    (state: string) => {
      onLockedCardStateChange?.(state);
    },
    [onLockedCardStateChange]
  );
  /** 用户群拓展能力 */
  const { data: userScale } = useRequest(
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1UserScaleNiceUsingGet1({
        conBrandId,
      });
      const result = { ...res.data.data, userNiceEmpty: false };
      result.userNiceEmpty =
        !res.data.data?.userNiceList?.length || res.data.data?.userNiceList?.every(item => !item?.value);
      return result;
    },
    {
      refreshDeps: [brandLockState],
    }
  );
  /** 线下门店分布 */
  const { data: offlineStore = {} } = useRequest(
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1BrandStoreUsingGet1({
        conBrandId,
      });
      return res.data.data;
    },
    {
      refreshDeps: [brandLockState],
    }
  );
  /** 消费者投诉指数/社交媒体搜索热度 */
  const { data: esgAndHotSerch } = useRequest(
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1EsgAndHotSearchUsingGet1({
        conBrandId,
      });
      const { consumerComplaintTotalNum, conBrandEsgList, brandHotSearchList } = res.data.data;
      const esgListData =
        conBrandEsgList?.map(item => {
          return { ...item, countTimeMonth: '' + item.countTimeMonth, dataTypeName: '新增投诉指数' };
        }) || [];
      const brandHotEmpty = !brandHotSearchList.length || brandHotSearchList?.every(item => !item?.value);
      const esgListEmpty =
        !esgListData || !esgListData.length || esgListData?.every(item => !item?.consumerComplaintNum);
      return {
        consumerComplaintTotalNum,
        esgListData,
        brandHotSearchList,
        brandHotEmpty,
        esgListEmpty,
      };
      // return res.data.data;
    },
    {
      refreshDeps: [brandLockState],
    }
  );
  /** 社交用户舆情值 */
  const { data: userOpinion } = useRequest(
    // async ({ month = new Date().getMonth(), yearStr = new Date().getFullYear() } = {}) => {
    async () => {
      const res = await brandDetailController.conBrandDetailsWebControllerV1SocialUserOpinionUsingGet1({
        conBrandId,
        // month,
        // yearStr,
      });
      const { brandEvaluateList, socialUserOpinionList } = res.data.data;
      const recentMaxCountTime = Math.max.apply(
        null,
        brandEvaluateList.map(e => new Date(e.countTimeStr))
      );
      return {
        /** 品牌正负面数据, 只显示最近一个月*/
        brandEvaluateList: brandEvaluateList.filter(
          e => e.countTimeStr === moment(recentMaxCountTime).format('YYYY-MM')
        ),
        /** 社交用户舆情 */
        socialUserOpinionList: socialUserOpinionList?.map(item => ({ ...item, name: item.dataTypeName })) || [],
      };
    },
    {
      refreshDeps: [brandLockState],
    }
  );

  return (
    !!token && (
      <>
        <ChartHeader
          title={
            <h1 className={styles.moduleTitle}>
              用户发展能力
              <TooltipBase
                title="由品牌社交平台官号粉丝规模、用户群拓展能力、品牌正向舆情、社交媒体搜索热度等维度搭建的数学模型计算得出。"
                placement="right"
              />
            </h1>
          }
          titleFontSize={18}
          className={styles.heaeder}
        />
        <div className={classNames('flex-row-nowrap', 'flex-between-start')}>
          <Chart
            title={
              <h2 className={styles.chartTitle}>
                社交平台官号粉丝规模
                <TooltipBase
                  title={
                    <>
                      <p>品牌多平台官方账号的粉丝数量总和。数据已脱敏处理。</p>
                      <p>数据来源：CBNData数据库</p>
                    </>
                  }
                  placement="right"
                />
              </h2>
            }
            titleFontSize={16}
            panelClassName={styles.leftContent}
            className={styles.title}
          >
            <div
              className={classNames('flex-row-nowrap', 'flex-between-start', styles.tag)}
              style={{ marginBottom: '10px' }}
            >
              <TitleTag style={{ marginBottom: 0 }}>官号总粉丝规模指数</TitleTag>
              {!totalFans ? (
                <span className={styles.emptyStateText}>暂无数据</span>
              ) : (
                <span className="reddish-orange-text-14">{totalFans}</span>
              )}
            </div>
            <LineAndBarMixedChart
              height={210}
              barChartData={fansList}
              xAxisName="countTimeStr"
              yAxisNameOfBarChart="value"
              scale={searchClotChartScale}
              barChartGroupBy="dataTypeName"
              intervalColor={colors}
            />
            {/* <LineChart
              data={fansList}
              xField="countTimeStr"
              yField="value"
              lField="dataTypeName"
              // updownKey="growRate"
              height={210}
            /> */}
          </Chart>
          <Chart
            title="社交平台官号粉丝数量占比"
            titleFontSize={16}
            panelClassName={styles.rightContent}
            className={styles.title}
          >
            <Pie
              height={254}
              data={fansRateList}
              xField="value"
              yField="dataTypeName"
              tooltip={(_title, items) => {
                return (
                  <div className={styles.tooltip}>
                    {/* <p>
                      {items[0]?.name}粉丝数量：<span>{items[0]?.data?.value}</span>
                    </p> */}
                    <p>
                      {/* TODO: 后端返回时间 */}
                      {/* {} */}
                      {items[0]?.name}粉丝占比：<span>{items[0]?.data?.totalRate}%</span>
                    </p>
                  </div>
                );
              }}
            />
          </Chart>
        </div>
        <Chart
          title={
            <h2 className={styles.chartTitle}>
              用户群拓展能力
              <TooltipBase
                title={
                  <>
                    <p>小红书平台中品牌联名与代言的声量数据，反映品牌营销破圈拓展用户的能力。数据已脱敏处理。</p>
                    <p>数据来源：千瓜数据</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          titleFontSize={16}
          dateSubTitle={false}
          panelClassName={styles.developmentChart}
          className={styles.title}
        >
          {brandLockState !== 'unlocked' ? (
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FlCaIEGkUG6pzWVMUHXItHMfwam5.png"
              explanation="此处为品牌联名和代言社交声量，以反映品牌线上用户群拓展能力
                若想查看该信息，请先开通中国新消费品牌数据库会员"
              state={brandLockState as LockCardState}
              className={styles.positiveFeedbackLockedCard}
              explanationClassName={styles.positiveFeedbackLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : userScale?.userNiceEmpty ? (
            <CustomEmpty />
          ) : (
            <LineAndBarMixedChart
              height={280}
              barChartData={userScale?.userNiceList}
              xAxisName="countTimeStr"
              yAxisNameOfBarChart="value"
              scale={searchClotChartScale}
              barChartGroupBy="dataTypeName"
              intervalColor={colors}
              interverProps={{ size: 80 }}
            />
            // <LineChart
            //   data={userScale?.userNiceList}
            //   xField="countTimeStr"
            //   yField="value"
            //   lField="dataTypeName"
            //   height={254}
            // />
          )}
        </Chart>
        <Chart
          title={
            <h2 className={styles.chartTitle}>
              线下门店分布
              <TooltipBase
                title={
                  <>
                    <p>品牌线下门店总数与省市分布情况。数据已脱敏处理。</p>
                    <p>数据来源：百炼店店通</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          titleFontSize={16}
          panelClassName={styles.developmentChart}
          className={styles.title}
        >
          {brandLockState !== 'unlocked' ? (
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FsRah3lsiAgmDdJ_pdBBPIxzA8nf.png"
              explanation="此处为品牌线下门店数量、分布及评分等数据，以反映品牌线下用户群拓展能力
                若想查看该信息，请先开通中国新消费品牌数据库会员"
              state={brandLockState as LockCardState}
              className={styles.storeLockedCard}
              explanationClassName={styles.storeLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : (
            <div className={classNames('flex-row-nowrap', 'flex-between-start', styles.gapStyle)}>
              <div className={styles.leftContent} style={{ flex: 36, padding: '0 40px 0 0' }}>
                <div className={classNames('flex-row-nowrap', 'flex-between-start', styles.tag)}>
                  <TitleTag style={{ marginBottom: 0 }}>线下门店总数</TitleTag>
                  {!offlineStore?.storeTotalNum ? (
                    <span className={styles.emptyStateText}>暂无数据</span>
                  ) : (
                    <span className={styles.num}>{offlineStore?.storeTotalNum}</span>
                  )}
                </div>
                {!offlineStore?.storeTotalNum ? (
                  <CustomEmpty className={styles.emptyState} />
                ) : (
                  <MapChart data={offlineStore?.brandStoreCityList} height={254} />
                )}
              </div>
              <Chart
                title="线下门店分布TOP5省市"
                titleFontSize={16}
                panelClassName={styles.storeNumChart}
                className={styles.title}
              >
                {!offlineStore?.storeTotalNum ? (
                  <CustomEmpty className={styles.emptyState} />
                ) : (
                  <Bar
                    height={254}
                    tooltipName="数量"
                    data={offlineStore?.brandStoreChartsList}
                    xField="province"
                    yField="storeNum"
                    padding={[20, 80, 40, 50]}
                  />
                )}
              </Chart>
            </div>
          )}
        </Chart>
        <div className="flex-row-nowrap flex-between-start">
          <Chart
            title={
              <div className={classNames('flex-row-nowrap', 'flex-start-center')}>
                <h2 className={styles.chartTitle}>
                  消费者投诉指数
                  <TooltipBase
                    title={
                      <>
                        <p>黑猫投诉平台中，消费者对于品牌的投诉信息数量。数据已脱敏处理。</p>
                        <p>数据来源：CBNData数据库</p>
                      </>
                    }
                    placement="right"
                  />
                </h2>
                {brandLockState === 'unlocked' && (
                  <div
                    style={{ marginLeft: '8px' }}
                    className={classNames('flex-row-nowrap', 'flex-between-start', styles.tag)}
                  >
                    <TitleTag style={{ marginBottom: 0 }}>消费者投诉指数</TitleTag>
                    {!esgAndHotSerch?.consumerComplaintTotalNum ? (
                      <span className={styles.emptyStateText}>暂无数据</span>
                    ) : (
                      <span className={styles.num}>{esgAndHotSerch?.consumerComplaintTotalNum}</span>
                    )}
                  </div>
                )}
              </div>
            }
            panelClassName={styles.consumerComplaintsChart}
            className={styles.consumerComplaintsChartTitle}
          >
            {brandLockState !== 'unlocked' ? (
              <LockedCard
                conBrandId={conBrandId}
                backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FiAOS_SHgbjlc9_Sh_q6gm88FPVF.png"
                explanation={
                  <>
                    <span>此处为品牌受到品牌消费者投诉的数据</span>
                    <span>若想查看该信息，请先开通中国新消费品牌数据库会员</span>
                  </>
                }
                state={brandLockState as LockCardState}
                className={styles.complaintLockedCard}
                explanationClassName={styles.complaintLockedExplanation}
                onStateChange={onStateChange}
              />
            ) : esgAndHotSerch?.esgListEmpty ? (
              <CustomEmpty className={styles.emptyState} />
            ) : (
              <LineAndBarMixedChart
                height={280}
                barChartData={esgAndHotSerch?.esgListData}
                // lineChartData={esgAndHotSerch?.esgListData}
                xAxisName="countTimeMonth"
                yAxisNameOfBarChart="consumerComplaintNum"
                // yAxisNameOfLineChart="contrastChangeValue"
                scale={scale}
                barChartGroupBy="dataTypeName"
                intervalColor={colors}
                legendProps={{
                  // custom: true,
                  // name: 'dataTypeName',
                  padding: [14, 0, 11, 0],
                  itemName: {
                    style: {
                      fill: '#999',
                    },
                  },
                  // items: [
                  //   {
                  //     name: '新增投诉指数',
                  //     value: 'scaleValue',
                  //     marker: { symbol: 'square', style: { fill: '#ffc4b2' } },
                  //   },
                  // {
                  //   name: '新增投诉指数增速',
                  //   value: 'increaseVaue',
                  //   marker: {
                  //     symbol,
                  //     style: {
                  //       stroke: '#f75c2f',
                  //       lineWidth: 2,
                  //       fill: '#f75c2f',
                  //     },
                  //   },
                  // },
                  // ],
                  // onChange(e, chart) {
                  //   const item = e.item;
                  //   const value = item.value;
                  //   const checked = !item.unchecked;
                  //   const geoms = chart.geometries;

                  //   for (let i = 0; i < geoms.length; i++) {
                  //     const geom = geoms[i];
                  //     if (geom.getYScale().field === value) {
                  //       if (checked) {
                  //         geom.show();
                  //       } else {
                  //         geom.hide();
                  //       }
                  //     }
                  //   }
                  // },
                }}
                interverProps={{ size: 16, shape: 'border-radius' }}
                // interverProps={{ size: 16, color: '#ffc4b2', shape: 'border-radius' }}
                // lineProps={{ color: '#f75c2f' }}
                // pointProps={{ size: 3, color: '#f75c2f' }}
              />
            )}
          </Chart>

          <Chart
            title={
              <h2 className={styles.chartTitle}>
                品牌舆情分析
                <TooltipBase
                  title={
                    <>
                      <p>小红书平台中品牌相关笔记的评论舆情正负向占比。</p>
                      <p>数据来源：千瓜数据</p>
                    </>
                  }
                  placement="right"
                />
              </h2>
            }
            className={styles.publicSentimentChartTitle}
            titleFontSize={16}
            panelClassName={styles.publicSentimentChart}
          >
            {brandLockState !== 'unlocked' ? (
              <LockedCard
                conBrandId={conBrandId}
                backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FiL25WjxQVdKw2G29MnuGuFhR1Zi.png"
                explanation={
                  <>
                    <span>此处为品牌受到品牌消费者投诉的数据</span>
                    <span>若想查看该信息，请先开通中国新消费品牌数据库会员</span>
                  </>
                }
                state={brandLockState as LockCardState}
                className={styles.complaintLockedCard}
                explanationClassName={styles.complaintLockedExplanation}
                onStateChange={onStateChange}
              />
            ) : (
              <Pie
                height={266}
                data={userOpinion?.brandEvaluateList}
                xField="value"
                yField="dataTypeName"
                tooltip={(_title, items) => {
                  return (
                    <div className={styles.tooltip}>
                      <p>
                        {items[0]?.name}：<span>{items[0]?.data?.value}%</span>
                      </p>
                    </div>
                  );
                }}
              />
            )}
          </Chart>
        </div>
        {/* <Chart
          title={
            <div className={classNames('flex-row-nowrap', 'flex-start-center')}>
              <h2>消费者投诉指数</h2>
              <div
                style={{ marginLeft: '8px' }}
                className={classNames('flex-row-nowrap', 'flex-between-start', styles.tag)}
              >
                <TitleTag style={{ marginBottom: 0 }}>消费者投诉指数</TitleTag>
                {!esgAndHotSerch?.consumerComplaintTotalNum ? (
                  <span className={styles.emptyStateText}>暂无数据</span>
                ) : (
                  <span className={styles.num}>{esgAndHotSerch?.consumerComplaintTotalNum}</span>
                )}
              </div>
            </div>
          }
          panelClassName={styles.developmentChart}
          className={styles.title}
        >
          {brandLockState !== 'unlocked' ? (
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FiAOS_SHgbjlc9_Sh_q6gm88FPVF.png"
              explanation={
                <>
                  <span>此处为品牌受到品牌消费者投诉的数据</span>
                  <span>若想查看该信息，请先开通中国新消费品牌数据库会员</span>
                </>
              }
              state={brandLockState as LockCardState}
              className={styles.complaintLockedCard}
              explanationClassName={styles.complaintLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : !esgAndHotSerch?.esgListData || !esgAndHotSerch?.esgListData.length ? (
            <CustomEmpty className={styles.emptyState} />
          ) : (
            <LineAndBarMixedChart
              barChartData={esgAndHotSerch.esgListData}
              lineChartData={esgAndHotSerch.esgListData}
              scale={scale}
              xAxisName="countTimeMonth"
              yAxisNameOfBarChart="consumerComplaintNum"
              yAxisNameOfLineChart="contrastChangeValue"
              legendProps={{
                custom: true,
                padding: [14, 0, 19, 0],
                items: [
                  {
                    name: '新增投诉指数',
                    value: 'scaleValue',
                    marker: { symbol: 'square', style: { fill: '#ffc4b2' } },
                  },
                  {
                    name: '新增投诉指数增速',
                    value: 'increaseVaue',
                    marker: {
                      symbol,
                      style: {
                        stroke: '#f75c2f',
                        lineWidth: 2,
                        fill: '#f75c2f',
                      },
                    },
                  },
                ],
                onChange(e, chart) {
                  const item = e.item;
                  const value = item.value;
                  const checked = !item.unchecked;
                  const geoms = chart.geometries;

                  for (let i = 0; i < geoms.length; i++) {
                    const geom = geoms[i];
                    if (geom.getYScale().field === value) {
                      if (checked) {
                        geom.show();
                      } else {
                        geom.hide();
                      }
                    }
                  }
                },
              }}
              interverProps={{ size: 16, color: '#ffc4b2', shape: 'border-radius' }}
              lineProps={{ color: '#f75c2f' }}
              pointProps={{ size: 3, color: '#f75c2f' }}
            />
          )}
        </Chart>
        {brandLockState !== 'unlocked' ? (
          <Chart
            title={
              <h2>
                社交用户舆情
                <ToolTip
                  title="由组织能力、经营能力、行业及品牌发展潜力、产品竞争能力、供应链管理能力、用户发展能力、营销能力等七大指标维度计算得出，综合评价品牌的增长潜力"
                  placement="right"
                />
              </h2>
            }
            titleFontSize={16}
            panelClassName={styles.developmentChart}
          >
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="//assets-oss.cbndata.com/cbndata-refactor-fe/FiL25WjxQVdKw2G29MnuGuFhR1Zi.png"
              explanation={
                <>
                  <span>此处为品牌受到品牌消费者投诉的数据</span>
                  <span>若想查看该信息，请先开通中国新消费品牌数据库会员</span>
                </>
              }
              state={brandLockState as LockCardState}
              className={styles.complaintLockedCard}
              explanationClassName={styles.complaintLockedExplanation}
              onStateChange={onStateChange}
            />
          </Chart>
        ) : (
          <div className={classNames('flex-row-nowrap', 'flex-between-start', styles.developmentChart)}>
            <Chart
              title={
                <div className={classNames('flex-row-nowrap', 'flex-start-center')}>
                  <h2 className={styles.socialOpinionChartTitle}>社交用户舆情</h2>
                  <DatePicker
                    defaultValue={moment().subtract(1, 'M')}
                    onChange={value => {
                      const yearStr = value.year();
                      const month = value.month() + 1;
                      run({ yearStr, month });
                    }}
                    picker="month"
                  />
                </div>
              }
              titleFontSize={16}
              panelClassName={classNames(styles.leftContent, styles.socialOpinionChart)}
            >
              <WordCloud data={userOpinion?.socialUserOpinionList} height={257} />
            </Chart>
            <Chart
              title="品牌舆情分析"
              titleFontSize={16}
              panelClassName={classNames(styles.rightContent, styles.fansChart)}
              className={styles.title}
            >
              <Pie height={254} data={userOpinion?.brandEvaluateList} xField="value" yField="dataTypeName" />
            </Chart>
          </div>
        )} */}

        <Chart
          title={
            <h2 className={styles.chartTitle}>
              社交媒体热度
              <TooltipBase
                title={
                  <>
                    <p>小红书平台上品牌关键词搜索的每月热度均值。</p>
                    <p>数据来源：千瓜数据</p>
                  </>
                }
                placement="right"
              />
            </h2>
          }
          titleFontSize={16}
          panelClassName={styles.developmentChart}
          className={styles.title}
        >
          {brandLockState !== 'unlocked' ? (
            <LockedCard
              conBrandId={conBrandId}
              backgroundImgUrl="assets-oss.cbndata.com/cbndata-refactor-fe/Fjd95ILKWfhyRuXcXeYblE1ElHZW.png"
              explanation="此处为品牌传统媒体及社媒搜索热度数据，以反映用户认知度
                若想查看该信息，请先开通中国新消费品牌数据库会员"
              state={brandLockState as LockCardState}
              className={styles.searchClotLockedCard}
              explanationClassName={styles.searchCloutLockedExplanation}
              onStateChange={onStateChange}
            />
          ) : esgAndHotSerch?.brandHotEmpty ? (
            <CustomEmpty />
          ) : (
            <LineAndBarMixedChart
              height={280}
              barChartData={esgAndHotSerch?.brandHotSearchList}
              xAxisName="countTimeStr"
              yAxisNameOfBarChart="value"
              scale={searchClotChartScale}
              barChartGroupBy="dataTypeName"
              interverProps={{ size: 80 }}
              intervalColor={colors}
            />
            // <LineChart
            //   data={esgAndHotSerch?.brandHotSearchList}
            //   xField="countTimeStr"
            //   yField="value"
            //   lField="dataTypeName"
            //   height={254}
            // />
          )}
        </Chart>
        {/* )} */}
      </>
    )
  );
};

export default memo(Development);
